import type { WebResearchActivityDTO, WebResearchSynthesisStageDTO } from '@shared/schemas/web-tools';
import { safeWebCitationUrl } from './chat-web-citations';

export type WebActivityToolPart = {
  errorText?: string;
  input?: unknown;
  output?: unknown;
  state: string;
  type: string;
  toolName?: string;
};

export type WebResearchSnapshot = {
  /** Hostname of the page being read, or of the last page read. */
  currentSite: string | null;
  /** True when at least one web tool call failed. */
  hasToolError: boolean;
  /** True when a search failed because Staan, the only search provider, was unavailable. */
  searchUnavailable: boolean;
  /**
   * True while a web tool call or a typed research phase (planning, searching,
   * reading) is still running. A running synthesis is answer writing, not
   * research: it never makes the research pending.
   */
  hasPendingWork: boolean;
  /** True when a tool call (not a typed activity) is still pending. */
  hasPendingTool: boolean;
  hasRunningActivity: boolean;
  /** True once the typed `synthesizing` phase started: research is over. */
  synthesisStarted: boolean;
  /** Waiting state of a running synthesis (drafting, then source review), if any. */
  synthesisStage: WebResearchSynthesisStageDTO | null;
  /** Distinct sites successfully read, in reading order. */
  sites: string[];
  /** Whether there is anything to show at all. */
  hasActivity: boolean;
};

const WEB_TOOL_NAMES = new Set(['web_search', 'fetch_url']);
const WEB_SEARCH_PROVIDER_UNAVAILABLE = 'WEB_SEARCH_PROVIDER_UNAVAILABLE';

function toRecord(value: unknown) {
  return value && typeof value === 'object' ? value as Record<string, unknown> : null;
}

function getRawToolName(part: WebActivityToolPart) {
  return part.type === 'dynamic-tool'
    ? part.toolName ?? ''
    : part.type.replace(/^tool-/, '');
}

function isPendingState(state: string) {
  return state === 'input-streaming' || state === 'input-available' || state === 'approval-requested';
}

function isFailedPart(part: WebActivityToolPart) {
  return Boolean(part.errorText)
    || part.state === 'output-error'
    || part.state === 'output-denied'
    || toRecord(part.output)?.error === true;
}

/** Hostname without `www.` for a safe http(s) URL, otherwise null (no third-party lookup). */
export function getSiteName(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const safeUrl = safeWebCitationUrl(value);
  if (!safeUrl) {
    return null;
  }

  return new URL(safeUrl).hostname.replace(/^www\./, '') || null;
}

function getFetchSite(part: WebActivityToolPart) {
  const input = toRecord(part.input);
  const output = toRecord(part.output);
  // A still-streaming input may hold a truncated URL: never display it.
  const candidates = part.state === 'input-streaming'
    ? [output?.finalUrl, output?.url]
    : [output?.finalUrl, output?.url, input?.url];

  for (const candidate of candidates) {
    const site = getSiteName(candidate);
    if (site) {
      return site;
    }
  }

  return null;
}

const RESEARCH_PHASES = new Set<WebResearchActivityDTO['phase']>(['planning', 'searching', 'reading']);

/**
 * Latest state of each phase, in first-seen order. A live message may carry the
 * same phase twice (a provisional client message merged with the final one):
 * the later part is always the newer state.
 */
export function latestWebResearchActivities(activities: readonly WebResearchActivityDTO[]): WebResearchActivityDTO[] {
  const latest = new Map<WebResearchActivityDTO['phase'], WebResearchActivityDTO>();
  for (const activity of activities) {
    latest.set(activity.phase, activity);
  }
  return [...latest.values()];
}

export function summarizeWebResearch(
  rawActivities: WebResearchActivityDTO[],
  toolParts: WebActivityToolPart[],
): WebResearchSnapshot {
  const activities = latestWebResearchActivities(rawActivities);
  const webToolParts = toolParts.filter((part) => WEB_TOOL_NAMES.has(getRawToolName(part)));
  const fetchParts = webToolParts.filter((part) => getRawToolName(part) === 'fetch_url');
  const sites: string[] = [];
  let currentSite: string | null = null;

  for (const part of fetchParts) {
    const site = getFetchSite(part);
    if (!site) {
      continue;
    }

    currentSite = site;
    if (part.state === 'output-available' && !isFailedPart(part) && !sites.includes(site)) {
      sites.push(site);
    }
  }

  const hasPendingTool = webToolParts.some((part) => isPendingState(part.state));
  const hasRunningActivity = activities.some((activity) => activity.status === 'running');
  const hasRunningResearch = activities.some((activity) => (
    activity.status === 'running' && RESEARCH_PHASES.has(activity.phase)
  ));
  const synthesis = activities.find((activity) => activity.phase === 'synthesizing');

  // The research orchestrator reads pages without streaming tool parts: its typed
  // `reading` phase carries the host name of the page being read instead.
  const reading = activities.find((activity) => activity.phase === 'reading');
  const typedCurrentSite = reading?.phase === 'reading' ? reading.currentSite ?? null : null;

  return {
    currentSite: currentSite ?? typedCurrentSite,
    hasActivity: activities.length > 0 || webToolParts.length > 0,
    hasPendingTool,
    hasPendingWork: hasPendingTool || hasRunningResearch,
    hasRunningActivity,
    hasToolError: webToolParts.some(isFailedPart),
    searchUnavailable: webToolParts.some((part) => (
      getRawToolName(part) === 'web_search'
      && toRecord(part.output)?.code === WEB_SEARCH_PROVIDER_UNAVAILABLE
    )),
    sites,
    synthesisStage: synthesis?.phase === 'synthesizing' && synthesis.status === 'running'
      ? synthesis.stage ?? null
      : null,
    synthesisStarted: synthesis !== undefined,
  };
}

/** Research duration persisted by the backend on the `synthesizing` phase, if any. */
export function getPersistedResearchDuration(activities: WebResearchActivityDTO[]): number | null {
  const synthesis = latestWebResearchActivities(activities).find((activity) => activity.phase === 'synthesizing');
  return synthesis?.phase === 'synthesizing' && typeof synthesis.durationMs === 'number'
    ? synthesis.durationMs
    : null;
}

/** Typed `reading` count, used only when no consulted site could be derived from tool parts. */
export function getTypedPageCount(activities: WebResearchActivityDTO[]) {
  const reading = [...activities].reverse().find((activity) => activity.phase === 'reading');
  return reading?.phase === 'reading' ? reading.pageCount : 0;
}

export function formatConsultedSites(count: number) {
  return `${count} site${count > 1 ? 's' : ''} consulté${count > 1 ? 's' : ''}`;
}

export function formatResearchDuration(durationMs: number) {
  const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds} s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds} s`;
}
